import { FC, useEffect } from 'react';
import { Provider, useUnit } from 'effector-react/scope';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { getAutocompleteOnChange } from '../../lib/ get-autocomplete-on-change';
import { getAutocompleteValue } from '../../lib/get-autocomplete-value';
import { $data, $pending, getList as getListEvent, createScope } from './model';
import type { Status, GetList } from './types';

type Props = {
  multiple?: boolean;
} & GetList;

export const ResidentOwnershipStatusControl: FC<any> = ({
  value,
  setValue,
  error,
  label,
  required,
  readOnly,
  disabled,
  isError,
  multiple = false,
  residentType,
}) => {
  const [data, pending, getList] = useUnit([$data, $pending, getListEvent]);

  useEffect(() => {
    getList(residentType);
  }, [residentType]);

  const handleChange = (
    _: unknown,
    newValue: string | Status | (string | Status)[] | null
  ) => getAutocompleteOnChange(newValue, setValue, multiple);

  return (
    <FormControl required={required} disabled={disabled}>
      {label && (
        <FormLabel required={!readOnly && required} error={isError}>
          {label}
        </FormLabel>
      )}
      <Autocomplete
        multiple={multiple}
        disableCloseOnSelect={multiple}
        loading={pending}
        value={getAutocompleteValue(data, value, multiple)}
        options={!data ? [] : data}
        onChange={handleChange}
        ListboxComponent={Listbox}
        renderInput={(params): JSX.Element => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              readOnly,
              error: isError,
              endAdornment: readOnly ? null : (
                <>
                  {pending && <CircularProgress size={20} color="inherit" />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        readOnly={readOnly}
        disabled={disabled}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export const ResidentOwnershipStatusControlWrapper = withControl<Props>(
  ({ name, ...props }) => {
    const scope = createScope(name);

    return (
      <Provider value={scope}>
        <ResidentOwnershipStatusControl {...props} />
      </Provider>
    );
  }
);
